import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth'
import { auth } from '../lib/firebase'
import { useI18n } from '../i18n/useI18n'

/**
 * Firebase Auth カスタムアクションハンドラ (#850).
 *
 * Firebase Auth のデフォルト action URL は **アクセスした瞬間に oobCode を
 * 消費する** 構造で、メールサーバ(お名前.com / O365 SafeLinks / Proofpoint
 * 等)が「スパム/ウイルス対策」でリンクを事前 GET すると、ユーザーが
 * クリックする前に oobCode が消費されて
 * `Try verifying your email again` (`auth/invalid-action-code`) で失敗する。
 *
 * 本ページは Firebase Console の「アクション URL のカスタマイズ」で
 * 設定する自前ハンドラで、以下の点が default 動作と異なる:
 *
 *   - 描画時には oobCode を消費しない (= mail gateway の事前 GET で
 *     消費されない)
 *   - ユーザーがボタンをクリックして初めて `applyActionCode` を呼ぶ
 *
 * 対応モード (= Firebase が `?mode=...` で送ってくる種類):
 *   - `verifyEmail`: メールアドレスの確認 (本フロー #846 のメイン)
 *   - `resetPassword`: パスワードリセット
 *   - `recoverEmail`: メール変更の取り消し
 *   - `signIn` (= passwordless email-link sign-in): 未実装 → 既定ハンドラに丸投げ
 *
 * Refs:
 *   - Firebase 公式: https://firebase.google.com/docs/auth/custom-email-handler
 *   - 親 issue #846 / 親 epic #840 / カスタム実装 issue #850
 */

type Mode =
  | 'verifyEmail'
  | 'resetPassword'
  | 'recoverEmail'
  | 'revertSecondFactorAddition'
  | 'signIn'
  | 'unknown'

type Phase =
  | 'idle' // 初期表示 (ユーザーのクリック待ち)
  | 'working' // applyActionCode 実行中
  | 'done' // 成功
  | 'error' // 失敗

interface ErrorState {
  code?: string
  message: string
}

function parseMode(raw: string | null): Mode {
  if (
    raw === 'verifyEmail' ||
    raw === 'resetPassword' ||
    raw === 'recoverEmail' ||
    raw === 'revertSecondFactorAddition' ||
    raw === 'signIn'
  ) {
    return raw
  }
  return 'unknown'
}

/** AuthAction の共通カードラッパー (lint react-hooks/static-components 対応で
 *  外出し)。中央寄せ + max-w-sm + shadow-card の汎用 wrapper。 */
function ActionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-white px-6">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-card">
        {children}
      </div>
    </div>
  )
}

function errorMessageKey(code: string | undefined): string {
  // Firebase の代表的なエラーコードを i18n key にマッピング。
  // 想定外の code は generic に丸める。
  switch (code) {
    case 'auth/expired-action-code':
      return 'authAction.errorExpired'
    case 'auth/invalid-action-code':
      return 'authAction.errorInvalid'
    case 'auth/user-disabled':
      return 'authAction.errorUserDisabled'
    case 'auth/user-not-found':
      return 'authAction.errorUserNotFound'
    case 'auth/weak-password':
      return 'authAction.errorWeakPassword'
    default:
      return 'authAction.errorGeneric'
  }
}

export default function AuthAction() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useI18n()

  const mode = parseMode(params.get('mode'))
  const oobCode = params.get('oobCode') || ''
  const continueUrl = params.get('continueUrl') || ''

  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState<ErrorState | null>(null)
  // resetPassword モード用
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [emailHint, setEmailHint] = useState<string>('')

  // oobCode 欠落 / mode 不明 はゲートで返す。ここで applyActionCode を呼ぶと
  // 単に Firebase から `auth/invalid-action-code` が返るだけで diagnose しづらい
  // ので、フロント側で先回りして弾く。
  if (!oobCode || mode === 'unknown') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-white px-6">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-card text-center">
          <AlertCircle size={32} className="mx-auto text-yokoso-red" />
          <p className="mt-3 font-body text-sm text-text-primary">
            {t('authAction.errorInvalid')}
          </p>
        </div>
      </div>
    )
  }

  const handleVerifyEmail = async () => {
    if (phase === 'working') return
    setPhase('working')
    setError(null)
    try {
      await applyActionCode(auth, oobCode)
      // 現在のユーザーがログイン中なら reload して emailVerified=true を反映。
      // ログアウト状態でもメール側で完了するケースを許容するため失敗は無視。
      try {
        await auth.currentUser?.reload()
      } catch {
        // ignore
      }
      setPhase('done')
    } catch (e) {
      const code = (e as { code?: string })?.code
      console.error('[AuthAction] verifyEmail failed:', e)
      setError({ code, message: t(errorMessageKey(code)) })
      setPhase('error')
    }
  }

  const handleVerifyResetCode = async () => {
    // resetPassword 時、ボタンクリック前に対象 email を表示するため
    // ユーザーが見ている段階で email だけは取得する (これも oobCode を消費しない)。
    if (emailHint || phase === 'working') return
    try {
      const email = await verifyPasswordResetCode(auth, oobCode)
      setEmailHint(email)
    } catch (e) {
      const code = (e as { code?: string })?.code
      console.warn('[AuthAction] verifyPasswordResetCode failed:', e)
      setError({ code, message: t(errorMessageKey(code)) })
      setPhase('error')
    }
  }

  const handleResetPassword = async () => {
    if (phase === 'working') return
    if (!newPassword || newPassword !== confirmPassword) {
      setError({ message: t('authAction.passwordMismatch') })
      return
    }
    setPhase('working')
    setError(null)
    try {
      await confirmPasswordReset(auth, oobCode, newPassword)
      setPhase('done')
    } catch (e) {
      const code = (e as { code?: string })?.code
      console.error('[AuthAction] confirmPasswordReset failed:', e)
      setError({ code, message: t(errorMessageKey(code)) })
      setPhase('error')
    }
  }

  const handleRevertSecondFactor = async () => {
    // revertSecondFactorAddition (= 2 段階認証追加の取り消し) は applyActionCode で
    // revert される。本人が意図して追加していない 2FA を、メール内リンクから
    // 取り消すフロー。enroll 直後に Firebase が自動送信する確認メールから飛ぶ。
    if (phase === 'working') return
    setPhase('working')
    setError(null)
    try {
      await applyActionCode(auth, oobCode)
      setPhase('done')
    } catch (e) {
      const code = (e as { code?: string })?.code
      console.error('[AuthAction] revertSecondFactor failed:', e)
      setError({ code, message: t(errorMessageKey(code)) })
      setPhase('error')
    }
  }

  const handleRecoverEmail = async () => {
    // recoverEmail (= メールアドレス変更の取り消し) は applyActionCode と
    // 同じ API で revert される。
    if (phase === 'working') return
    setPhase('working')
    setError(null)
    try {
      // 取り消し前にどの email に戻るかを取得しておく (UX 用)。
      try {
        const info = await checkActionCode(auth, oobCode)
        setEmailHint(info.data.email || '')
      } catch {
        // ignore — applyActionCode は別途呼ぶ
      }
      await applyActionCode(auth, oobCode)
      setPhase('done')
    } catch (e) {
      const code = (e as { code?: string })?.code
      console.error('[AuthAction] recoverEmail failed:', e)
      setError({ code, message: t(errorMessageKey(code)) })
      setPhase('error')
    }
  }

  // signIn (= passwordless email-link) は本アプリ未使用のため、想定外モード
  // として error 表示。実装が必要になったら ここに分岐を追加。

  const goHome = () => {
    if (continueUrl) {
      // continueUrl は Firebase Console で許可済ドメインのみ受け付ける
      // (open redirect 対策)。location.href で離脱。
      window.location.href = continueUrl
    } else {
      navigate('/')
    }
  }

  // ---------- Render ----------
  // ---------- verifyEmail ----------

  if (mode === 'verifyEmail') {
    if (phase === 'done') {
      return (
        <ActionCard>
          <div className="text-center">
            <CheckCircle size={32} className="mx-auto text-matcha" />
            <h1 className="mt-3 font-display text-base font-semibold text-text-primary">
              {t('authAction.verifyEmailSuccessTitle')}
            </h1>
            <p className="mt-2 font-body text-sm text-text-secondary leading-relaxed">
              {t('authAction.verifyEmailSuccessDesc')}
            </p>
            <button
              onClick={goHome}
              className="mt-5 h-11 w-full rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect"
            >
              {t('authAction.continueToApp')}
            </button>
          </div>
        </ActionCard>
      )
    }
    if (phase === 'error') {
      return (
        <ActionCard>
          <div className="text-center">
            <AlertCircle size={32} className="mx-auto text-yokoso-red" />
            <p className="mt-3 font-body text-sm text-text-primary leading-relaxed">
              {error?.message || t('authAction.errorGeneric')}
            </p>
            <button
              onClick={goHome}
              className="mt-5 h-11 w-full rounded-xl border border-gray-200 font-body text-sm text-text-secondary tap-effect"
            >
              {t('authAction.backToApp')}
            </button>
          </div>
        </ActionCard>
      )
    }
    return (
      <ActionCard>
        <div className="flex flex-col items-center text-center">
          <Mail size={28} className="text-yokoso-red" />
          <h1 className="mt-3 font-display text-base font-semibold text-text-primary">
            {t('authAction.verifyEmailTitle')}
          </h1>
          <p className="mt-2 font-body text-xs text-text-secondary leading-relaxed">
            {t('authAction.verifyEmailDesc')}
          </p>
        </div>
        <button
          onClick={handleVerifyEmail}
          disabled={phase === 'working'}
          className="mt-5 h-11 w-full rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect disabled:opacity-50"
        >
          {phase === 'working' ? (
            <Loader2 size={16} className="mx-auto animate-spin" />
          ) : (
            t('authAction.verifyEmailButton')
          )}
        </button>
      </ActionCard>
    )
  }

  // ---------- resetPassword ----------

  if (mode === 'resetPassword') {
    // 初回レンダー時に email hint だけ取りに行く (oobCode 消費されない)。
    if (!emailHint && !error) {
      handleVerifyResetCode()
    }
    if (phase === 'done') {
      return (
        <ActionCard>
          <div className="text-center">
            <CheckCircle size={32} className="mx-auto text-matcha" />
            <p className="mt-3 font-body text-sm text-text-primary">
              {t('authAction.resetPasswordSuccess')}
            </p>
            <button
              onClick={() => navigate('/store/login')}
              className="mt-5 h-11 w-full rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect"
            >
              {t('authAction.goToLogin')}
            </button>
          </div>
        </ActionCard>
      )
    }
    if (phase === 'error') {
      return (
        <ActionCard>
          <div className="text-center">
            <AlertCircle size={32} className="mx-auto text-yokoso-red" />
            <p className="mt-3 font-body text-sm text-text-primary leading-relaxed">
              {error?.message || t('authAction.errorGeneric')}
            </p>
          </div>
        </ActionCard>
      )
    }
    return (
      <ActionCard>
        <h1 className="font-display text-base font-semibold text-text-primary text-center">
          {t('authAction.resetPasswordTitle')}
        </h1>
        {emailHint && (
          <p className="mt-2 font-body text-xs text-text-secondary text-center">
            {emailHint}
          </p>
        )}
        <input
          type="password"
          placeholder={t('authAction.newPasswordPlaceholder')}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="mt-4 h-12 w-full rounded-xl border border-gray-200 px-4 font-body text-sm focus:border-navy focus:outline-none"
        />
        <input
          type="password"
          placeholder={t('authAction.confirmPasswordPlaceholder')}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="mt-2 h-12 w-full rounded-xl border border-gray-200 px-4 font-body text-sm focus:border-navy focus:outline-none"
        />
        <button
          onClick={handleResetPassword}
          disabled={phase === 'working' || !newPassword || newPassword !== confirmPassword}
          className="mt-4 h-11 w-full rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect disabled:opacity-50"
        >
          {phase === 'working' ? (
            <Loader2 size={16} className="mx-auto animate-spin" />
          ) : (
            t('authAction.resetPasswordButton')
          )}
        </button>
      </ActionCard>
    )
  }

  // ---------- recoverEmail ----------

  if (mode === 'recoverEmail') {
    if (phase === 'done') {
      return (
        <ActionCard>
          <div className="text-center">
            <CheckCircle size={32} className="mx-auto text-matcha" />
            <p className="mt-3 font-body text-sm text-text-primary">
              {t('authAction.recoverEmailSuccess')}
              {emailHint && (
                <span className="block mt-1 text-xs text-text-secondary">{emailHint}</span>
              )}
            </p>
            <button
              onClick={goHome}
              className="mt-5 h-11 w-full rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect"
            >
              {t('authAction.continueToApp')}
            </button>
          </div>
        </ActionCard>
      )
    }
    if (phase === 'error') {
      return (
        <ActionCard>
          <div className="text-center">
            <AlertCircle size={32} className="mx-auto text-yokoso-red" />
            <p className="mt-3 font-body text-sm text-text-primary leading-relaxed">
              {error?.message || t('authAction.errorGeneric')}
            </p>
          </div>
        </ActionCard>
      )
    }
    return (
      <ActionCard>
        <div className="flex flex-col items-center text-center">
          <AlertCircle size={28} className="text-gold" />
          <h1 className="mt-3 font-display text-base font-semibold text-text-primary">
            {t('authAction.recoverEmailTitle')}
          </h1>
          <p className="mt-2 font-body text-xs text-text-secondary leading-relaxed">
            {t('authAction.recoverEmailDesc')}
          </p>
        </div>
        <button
          onClick={handleRecoverEmail}
          disabled={phase === 'working'}
          className="mt-5 h-11 w-full rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect disabled:opacity-50"
        >
          {phase === 'working' ? (
            <Loader2 size={16} className="mx-auto animate-spin" />
          ) : (
            t('authAction.recoverEmailButton')
          )}
        </button>
      </ActionCard>
    )
  }

  // ---------- revertSecondFactorAddition ----------
  // 2 段階認証 (TOTP 等) を追加した直後に Firebase が「追加されました、
  // 心当たり無ければ取り消し」通知を送る。そのリンクから飛んでくるモード。

  if (mode === 'revertSecondFactorAddition') {
    if (phase === 'done') {
      return (
        <ActionCard>
          <div className="text-center">
            <CheckCircle size={32} className="mx-auto text-matcha" />
            <h1 className="mt-3 font-display text-base font-semibold text-text-primary">
              {t('authAction.revertTotpSuccessTitle')}
            </h1>
            <p className="mt-2 font-body text-sm text-text-secondary leading-relaxed">
              {t('authAction.revertTotpSuccessDesc')}
            </p>
            <button
              onClick={goHome}
              className="mt-5 h-11 w-full rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect"
            >
              {t('authAction.backToApp')}
            </button>
          </div>
        </ActionCard>
      )
    }
    if (phase === 'error') {
      return (
        <ActionCard>
          <div className="text-center">
            <AlertCircle size={32} className="mx-auto text-yokoso-red" />
            <p className="mt-3 font-body text-sm text-text-primary leading-relaxed">
              {error?.message || t('authAction.errorGeneric')}
            </p>
            <button
              onClick={goHome}
              className="mt-5 h-11 w-full rounded-xl border border-gray-200 font-body text-sm text-text-secondary tap-effect"
            >
              {t('authAction.backToApp')}
            </button>
          </div>
        </ActionCard>
      )
    }
    return (
      <ActionCard>
        <div className="flex flex-col items-center text-center">
          <AlertCircle size={28} className="text-gold" />
          <h1 className="mt-3 font-display text-base font-semibold text-text-primary">
            {t('authAction.revertTotpTitle')}
          </h1>
          <p className="mt-2 font-body text-xs text-text-secondary leading-relaxed">
            {t('authAction.revertTotpDesc')}
          </p>
        </div>
        <button
          onClick={handleRevertSecondFactor}
          disabled={phase === 'working'}
          className="mt-5 h-11 w-full rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect disabled:opacity-50"
        >
          {phase === 'working' ? (
            <Loader2 size={16} className="mx-auto animate-spin" />
          ) : (
            t('authAction.revertTotpButton')
          )}
        </button>
      </ActionCard>
    )
  }

  // ---------- signIn / その他 (未対応) ----------

  return (
    <ActionCard>
      <div className="text-center">
        <AlertCircle size={32} className="mx-auto text-yokoso-red" />
        <p className="mt-3 font-body text-sm text-text-primary">
          {t('authAction.errorUnsupportedMode')}
        </p>
      </div>
    </ActionCard>
  )
}
